'use client';

import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { useMenuBar } from './MenuBarController';

const VIEWPORT_GUTTER_PX = 16;
const HEADER_TO_PANEL_GAP_PX = 6;

export const FLYOUT_LAYERS = {
  overlay: 9997,
  panel: 9998,
} as const;

/**
 * Panel sizing strategy:
 *  - 'wide' : spans full viewport width minus equal gutters (mega menus).
 *  - 'fit'  : panel sizes to its content, centered under the trigger and
 *             clamped inside the viewport gutters (simple dropdowns).
 */
export type FlyoutWidthMode = 'wide' | 'fit';

export interface FlyoutPanel {
  isOpen: boolean;
  isKeyboardOpen: boolean;
  id: string;
  triggerRef: (el: HTMLButtonElement | null) => void;
  panelRef: React.MutableRefObject<HTMLDivElement | null>;
  panelMaxHeight: string;
  openByPointer: () => void;
  openByKeyboard: () => void;
  close: () => void;
  toggleByPointer: () => void;
  cancelHoverClose: () => void;
  scheduleHoverClose: () => void;
  triggerProps: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  };
  panelProps: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  };
}

interface UseFlyoutPanelOptions {
  id: string;
  width?: FlyoutWidthMode;
  /** Minimum panel width when width='fit'. Default: max(trigger width, 240). */
  minWidth?: number;
  /** True when the current route lives under this menu — drives the resting indicator. */
  isActive?: boolean;
}

function getHeaderHeightPx(): number {
  if (typeof window === 'undefined') return 64;
  const css = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
  const parsed = parseInt(css || '0', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 64;
}

function getHeaderEl(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return document.querySelector('[data-header-bar]');
}

/**
 * Anchors a portal-rendered flyout to a single trigger button, sourcing its
 * open state from the shared MenuBarController. The hook itself is responsible
 * only for:
 *   - registering the trigger element with the controller (for the indicator),
 *   - keyboard navigation inside the panel,
 *   - measuring + positioning the panel under the header's bottom edge.
 */
export function useFlyoutPanel(options: UseFlyoutPanelOptions): FlyoutPanel {
  const { id, width = 'wide', minWidth, isActive = false } = options;

  const menubar = useMenuBar();
  const isOpen = menubar.activeId === id;
  const isKeyboardOpen = isOpen && menubar.isKeyboardOpen;

  const triggerElRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const setTriggerRef = useCallback(
    (el: HTMLButtonElement | null) => {
      triggerElRef.current = el;
      menubar.registerTrigger(id, el);
    },
    [menubar, id],
  );

  useEffect(() => {
    menubar.setRestingActive(id, isActive);
    return () => menubar.setRestingActive(id, false);
  }, [menubar, id, isActive]);

  const openByPointer = useCallback(() => {
    menubar.openMenu(id, 'pointer');
  }, [menubar, id]);

  const openByKeyboard = useCallback(() => {
    menubar.openMenu(id, 'keyboard');
  }, [menubar, id]);

  const close = useCallback(() => {
    menubar.closeMenu();
  }, [menubar]);

  const toggleByPointer = useCallback(() => {
    menubar.toggleMenu(id);
  }, [menubar, id]);

  const positionPanel = useCallback(() => {
    const trigger = triggerElRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;

    const headerEl = getHeaderEl();
    const headerRect = headerEl?.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const maxViewport = viewportWidth - VIEWPORT_GUTTER_PX * 2;

    const panelTop = (headerRect?.bottom ?? triggerRect.bottom) + HEADER_TO_PANEL_GAP_PX;

    let panelLeft: number;
    let panelWidth: number;

    if (width === 'wide') {
      panelLeft = VIEWPORT_GUTTER_PX;
      panelWidth = Math.max(0, maxViewport);
    } else {
      panel.style.width = 'max-content';
      const measuredWidth = panel.getBoundingClientRect().width;

      const desiredMin = minWidth ?? Math.max(triggerRect.width, 240);
      panelWidth = Math.min(maxViewport, Math.max(measuredWidth, desiredMin));

      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      const idealLeft = triggerCenter - panelWidth / 2;
      panelLeft = Math.max(
        VIEWPORT_GUTTER_PX,
        Math.min(viewportWidth - VIEWPORT_GUTTER_PX - panelWidth, idealLeft),
      );
    }

    panel.style.left = `${panelLeft}px`;
    panel.style.top = `${panelTop}px`;
    panel.style.width = `${panelWidth}px`;
  }, [width, minWidth]);

  useEffect(() => {
    if (!isOpen) return;

    const onDocPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || triggerElRef.current?.contains(target)) return;
      menubar.closeMenu();
    };

    document.addEventListener('mousedown', onDocPointerDown);
    return () => document.removeEventListener('mousedown', onDocPointerDown);
  }, [isOpen, menubar]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    positionPanel();

    const onResize = () => positionPanel();
    const onScroll = () => positionPanel();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, { passive: true });

    if (isKeyboardOpen) {
      queueMicrotask(() => {
        const first = panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]');
        first?.focus();
      });
    }

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
    };
  }, [isOpen, isKeyboardOpen, positionPanel]);

  useEffect(() => {
    return () => {
      menubar.registerTrigger(id, null);
    };
  }, [menubar, id]);

  const triggerProps = {
    onMouseEnter: openByPointer,
    onMouseLeave: menubar.scheduleClose,
    onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (isOpen) menubar.closeMenu();
        else menubar.openMenu(id, 'keyboard');
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        menubar.openMenu(id, 'keyboard');
      } else if (e.key === 'Escape') {
        menubar.closeMenu();
      }
    },
  };

  const panelProps = {
    onMouseEnter: menubar.cancelClose,
    onMouseLeave: menubar.scheduleClose,
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
      const items = Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
      );
      if (items.length === 0) return;

      const currentIdx = items.indexOf(document.activeElement as HTMLElement);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          items[(currentIdx + 1) % items.length]?.focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          items[(currentIdx - 1 + items.length) % items.length]?.focus();
          break;
        case 'Home':
          e.preventDefault();
          items[0]?.focus();
          break;
        case 'End':
          e.preventDefault();
          items[items.length - 1]?.focus();
          break;
        case 'Escape':
          e.preventDefault();
          menubar.closeMenu();
          triggerElRef.current?.focus();
          break;
      }
    },
  };

  const headerHeight = typeof window === 'undefined' ? 64 : getHeaderHeightPx();
  const panelMaxHeight = `calc(100vh - ${headerHeight}px - 24px)`;

  return {
    isOpen,
    isKeyboardOpen,
    id,
    triggerRef: setTriggerRef,
    panelRef,
    panelMaxHeight,
    openByPointer,
    openByKeyboard,
    close,
    toggleByPointer,
    cancelHoverClose: menubar.cancelClose,
    scheduleHoverClose: menubar.scheduleClose,
    triggerProps,
    panelProps,
  };
}
